import React, {useEffect, useMemo} from 'react'
import {v4 as uuidv4} from "uuid";

const setContainerTransforms = (containerId, elementId, zoom) => {
  const container = document.getElementById(containerId)
  const element = document.querySelector(`#${containerId} #${elementId}`)

  if (container) {
    container.style.transitionProperty = null
    container.style.transformOrigin = null
    container.style.transform = null
  }

  if (container && element) {
    const containerBounds = container.getBoundingClientRect();
    const elementBounds = element.getBoundingClientRect();
    const widthZoom = containerBounds.width / elementBounds.width;
    const heightZoom = containerBounds.height / elementBounds.height;
    const scale = zoom || Math.min(widthZoom, heightZoom);
    const scaledWidth = elementBounds.width * scale;
    const scaledHeight = elementBounds.height * scale;
    const elementOffsetX = ((containerBounds.width - scaledWidth) / 2) / scale;
    const elementOffsetY = ((containerBounds.height - scaledHeight) / 2) / scale;
    const left = containerBounds.left - elementBounds.left + elementOffsetX;
    const top = containerBounds.top - elementBounds.top + elementOffsetY;

    container.style.transitionProperty = "transform";
    container.style.transformOrigin = "top left";
    container.style.transform = `scale(${scale}) translate(${left}px, ${top}px)`;
  }
}

const ElementFocuser = (
  {
    zoom,
    style,
    elementId,
    children,
  }
) => {
  const containerId = useMemo(() => `ISW_${uuidv4().replaceAll("-", "")}`, [])

  useEffect(() => {
    setContainerTransforms(containerId, elementId, zoom)
  }, [
    zoom,
    containerId,
    elementId,
  ])

  useEffect(() => {
    const onWindowResize = (event) => {
      setContainerTransforms(containerId, elementId, zoom)
    }

    window.addEventListener('resize', onWindowResize, true);
    window.addEventListener('redraw-element-focuser', onWindowResize, true);

    return () => {
      window.removeEventListener('resize', onWindowResize)
      window.removeEventListener('redraw-element-focuser', onWindowResize)
    }
  }, [
    zoom,
    containerId,
    elementId,
  ])

  return <>
    <div style={style}>
      <div id={containerId}>
        {children}
      </div>
    </div>
  </>
}

const InteractiveSvgWrapper = (
  {
    children,
    interactiveElementIds,
    interactiveElementClasses,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onElementClicked,
    style,
    elementId,
    zoom
  }
) => {
  const id = useMemo(() => `ISW_${uuidv4().replaceAll("-", "")}`, [])

  useEffect(() => {
    const allClickListeners = []
    const allMouseEnterListeners = []
    const allMouseleaveListeners = []

    const onClickListener = (element) => {
      const listener = (event) => {
        onClick && onClick(element, event)
      }
      allClickListeners.push({element, listener})
      return listener
    }
    const onMouseEnterListener = (element) => {
      const listener = (event) => {
        onMouseEnter && onMouseEnter(element, event)
      }
      allMouseEnterListeners.push({element, listener})
      return listener
    }
    const onMouseLeaveListener = (element) => {
      const listener = (event) => {
        onMouseLeave && onMouseLeave(element, event)
      }
      allMouseleaveListeners.push({element, listener})
      return listener
    }

    interactiveElementIds?.forEach(interactiveElementId => {
      const element = document.querySelector(`#${id} #${interactiveElementId}`)
      if (element) {
        element.addEventListener("click", onClickListener(element))
        element.addEventListener("mouseenter", onMouseEnterListener(element))
        element.addEventListener("mouseleave", onMouseLeaveListener(element))
      }
    })

    interactiveElementClasses?.forEach(interactiveElementId => {
      const element = document.querySelector(`#${id} .${interactiveElementId}`)
      if (element) {
        element.addEventListener("click", onClickListener(element))
        element.addEventListener("mouseenter", onMouseEnterListener(element))
        element.addEventListener("mouseleave", onMouseLeaveListener(element))
      }
    })

    return () => {
      allClickListeners.forEach(listener => {
        listener.element.removeEventListener("click", listener.listener)
      })
      allMouseEnterListeners.forEach(listener => {
        listener.element.removeEventListener("mouseenter", listener.listener)
      })
      allMouseleaveListeners.forEach(listener => {
        listener.element.removeEventListener("mouseleave", listener.listener)
      })
    }
  }, [
    id,
    children,
    interactiveElementIds,
    onClick,
    onMouseEnter,
    onMouseLeave,
    onElementClicked,
  ])

  return <div id={id}>
    <ElementFocuser
      zoom={zoom}
      style={style}
      elementId={elementId}
    >
      {children}
    </ElementFocuser>
  </div>
}

export default InteractiveSvgWrapper;